import type { PriceIndexPoint } from "@/types/public-data"

const PADDING = { top: 20, right: 20, bottom: 40, left: 80 }
const VIEW_WIDTH = 600
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

function formatK(value: number): string {
  if (value >= 1_000_000) {
    const m = value / 1_000_000
    return m % 1 === 0 ? `${m}m` : `${m.toFixed(1)}m`
  }
  const k = value / 1_000
  return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`
}

function formatMonth(dateString: string): string {
  const d = new Date(dateString)
  const month = MONTHS[d.getMonth()]
  const year = String(d.getFullYear()).slice(2)
  return `${month} '${year}`
}

interface PriceTrendChartProps {
  data: PriceIndexPoint[]
  height?: number
}

export default function PriceTrendChart({
  data,
  height = 200,
}: PriceTrendChartProps) {
  if (data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate">
        No price data available.
      </p>
    )
  }

  const sorted = [...data].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )

  const prices = sorted.map((d) => d.averagePrice)
  const minPrice = Math.min(...prices)
  const maxPrice = Math.max(...prices)
  const priceRange = maxPrice - minPrice || 1

  const chartWidth = VIEW_WIDTH - PADDING.left - PADDING.right
  const chartHeight = height - PADDING.top - PADDING.bottom

  function toX(index: number): number {
    return PADDING.left + (index / Math.max(sorted.length - 1, 1)) * chartWidth
  }

  function toY(price: number): number {
    return (
      PADDING.top + chartHeight - ((price - minPrice) / priceRange) * chartHeight
    )
  }

  // Build polyline points
  const linePoints = sorted
    .map((d, i) => `${toX(i)},${toY(d.averagePrice)}`)
    .join(" ")

  // Area fill path
  const areaPath = [
    `M ${toX(0)},${toY(sorted[0].averagePrice)}`,
    ...sorted.slice(1).map((d, i) => `L ${toX(i + 1)},${toY(d.averagePrice)}`),
    `L ${toX(sorted.length - 1)},${PADDING.top + chartHeight}`,
    `L ${toX(0)},${PADDING.top + chartHeight}`,
    "Z",
  ].join(" ")

  // Y-axis ticks (5 ticks)
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const value = minPrice + (priceRange * i) / 4
    return { value, y: toY(value) }
  })

  // X-axis labels: first, middle, last
  const xLabelIndices = [
    0,
    Math.floor(sorted.length / 2),
    sorted.length - 1,
  ].filter((v, i, arr) => arr.indexOf(v) === i)

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
      className="w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Grid lines */}
      {yTicks.map((tick) => (
        <line
          key={tick.value}
          x1={PADDING.left}
          y1={tick.y}
          x2={VIEW_WIDTH - PADDING.right}
          y2={tick.y}
          stroke="#E8E4DF"
          strokeWidth={1}
        />
      ))}

      {/* Y-axis labels */}
      {yTicks.map((tick) => (
        <text
          key={`label-${tick.value}`}
          x={PADDING.left - 10}
          y={tick.y + 4}
          textAnchor="end"
          className="fill-slate text-[11px]"
        >
          {`\u00A3${formatK(tick.value)}`}
        </text>
      ))}

      {/* Area fill */}
      <path d={areaPath} fill="#E8E4DF" opacity={0.4} />

      {/* Line */}
      <polyline
        points={linePoints}
        fill="none"
        stroke="#1A1A1A"
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Data points */}
      {sorted.map((d, i) => (
        <circle
          key={d.date}
          cx={toX(i)}
          cy={toY(d.averagePrice)}
          r={3}
          fill="#1A1A1A"
        />
      ))}

      {/* X-axis labels */}
      {xLabelIndices.map((idx) => (
        <text
          key={`x-${idx}`}
          x={toX(idx)}
          y={height - 8}
          textAnchor="middle"
          className="fill-slate text-[11px]"
        >
          {formatMonth(sorted[idx].date)}
        </text>
      ))}
    </svg>
  )
}
